import React, { useState, useEffect } from 'react';
import { MdSave, MdSettings, MdCheckCircle, MdErrorOutline } from 'react-icons/md';
import { db } from '../../services/firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';

const ToggleSwitch = ({ checked, onChange, label, description }) => (
  <label className="flex items-center justify-between cursor-pointer p-4 hover:bg-gray-50 rounded-xl transition-colors">
    <div className="flex-1 pr-6">
      <span className="font-semibold text-darkNavy text-base">{label}</span>
      {description && <p className="text-sm text-gray-500 mt-1">{description}</p>}
    </div>
    <div className="relative inline-block w-12 h-7 rounded-full transition-colors duration-300 ease-in-out flex-shrink-0"
         style={{ backgroundColor: checked ? '#4F46E5' : '#E5E7EB' }}>
      <span
        className={`absolute top-1 left-1 bg-white w-5 h-5 rounded-full shadow-md transform transition-transform duration-300 ease-in-out ${
          checked ? 'translate-x-5' : 'translate-x-0'
        }`}
      />
      <input type="checkbox" className="hidden" checked={checked} onChange={(e) => onChange(e.target.checked)} />
    </div>
  </label>
);

const InputField = ({ label, type = 'text', value, onChange, placeholder, hint, isTextArea = false }) => (
  <div className="flex flex-col">
    <label className="text-sm font-semibold text-gray-700 mb-2">{label}</label>
    {isTextArea ? (
      <textarea
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        rows={3}
        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-all resize-none shadow-sm"
      />
    ) : (
      <input
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-all shadow-sm"
      />
    )}
    {hint && <p className="text-xs text-gray-500 mt-1.5 ml-1">{hint}</p>}
  </div>
);

const Settings = () => {
  const [settings, setSettings] = useState({
    appName: 'Smart Brain',
    appDescription: 'Comprehensive Learning Platform',
    supportEmail: 'support@smartbrain.com',
    maintenanceMode: false,
    paystackPublicKey: '',
    paystackSecretKey: '',
    enablePayments: true,
    enableEmailNotifications: true,
    enablePushNotifications: false,
    privacyPolicyUrl: '',
    termsUrl: '',
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null); // 'success' | 'error' | null

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const docRef = doc(db, 'settings', 'general');
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setSettings((prev) => ({ ...prev, ...docSnap.data() }));
        }
      } catch (err) {
        console.error('Error fetching settings:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setSaveStatus(null);
    try {
      await setDoc(doc(db, 'settings', 'general'), settings, { merge: true });
      setSaveStatus('success');
      setTimeout(() => setSaveStatus(null), 3000);
    } catch (err) {
      console.error('Error saving settings:', err);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus(null), 4000);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="w-full h-full flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-5xl mx-auto pb-12 animate-fade-in relative">
      {/* Save Notification Toast */}
      {saveStatus && (
        <div className={`fixed top-8 right-8 z-50 flex items-center gap-3 px-6 py-4 rounded-xl shadow-2xl transform transition-all duration-300 translate-y-0 opacity-100 ${
          saveStatus === 'success' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-red-50 text-red-700 border border-red-200'
        }`}>
          {saveStatus === 'success' ? <MdCheckCircle size={24} /> : <MdErrorOutline size={24} />}
          <span className="font-bold">{saveStatus === 'success' ? 'Settings Saved Successfully!' : 'Failed to save settings.'}</span>
        </div>
      )}

      {/* Page Header */}
      <div className="mb-8 flex justify-between items-end">
        <div>
          <h1 className="text-3xl font-extrabold text-darkNavy mb-2 tracking-tight">Platform Settings</h1>
          <p className="text-gray-500 font-medium">Manage your application configurations and integrations.</p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="hidden md:flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-primary to-indigo-600 text-white rounded-xl font-bold hover:shadow-lg transform hover:-translate-y-0.5 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <MdSave size={20} />}
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      <div className="space-y-8">
        {/* App Information Card */}
        <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden transition-all hover:shadow-md">
          <div className="bg-gradient-to-r from-gray-50 to-white px-8 py-6 border-b border-gray-100 flex items-center gap-4">
            <div className="w-12 h-12 bg-primary/10 rounded-2xl flex items-center justify-center shadow-sm">
              <MdSettings className="text-primary text-2xl" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-darkNavy">General Information</h2>
              <p className="text-sm text-gray-500 mt-0.5">Basic details about your application</p>
            </div>
          </div>
          
          <div className="p-8 space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <InputField
                label="Application Name"
                value={settings.appName}
                onChange={(e) => setSettings({ ...settings, appName: e.target.value })}
                placeholder="e.g. Smart Brain"
              />
              <InputField
                label="Support Email Address"
                type="email"
                value={settings.supportEmail}
                onChange={(e) => setSettings({ ...settings, supportEmail: e.target.value })}
                placeholder="support@example.com"
              />
            </div>
            <InputField
              label="Application Description"
              isTextArea
              value={settings.appDescription}
              onChange={(e) => setSettings({ ...settings, appDescription: e.target.value })}
              placeholder="Brief description of the app for meta tags and SEO."
            />
            <div className="pt-4 border-t border-gray-100">
              <ToggleSwitch
                label="Maintenance Mode"
                description="Temporarily disable student access and show a 'down for maintenance' page."
                checked={settings.maintenanceMode}
                onChange={(val) => setSettings({ ...settings, maintenanceMode: val })}
              />
            </div>
          </div>
        </div>

        {/* Integration & Payments */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden transition-all hover:shadow-md">
            <div className="px-8 py-6 border-b border-gray-100 bg-gray-50/50">
              <h2 className="text-xl font-bold text-darkNavy">Payment Gateway</h2>
              <p className="text-sm text-gray-500 mt-0.5">Paystack API integration settings</p>
            </div>
            <div className="p-8 space-y-6">
              <ToggleSwitch
                label="Enable Payments"
                description="Allow students to purchase subscriptions."
                checked={settings.enablePayments}
                onChange={(val) => setSettings({ ...settings, enablePayments: val })}
              />
              <div className="pt-4 border-t border-gray-100 space-y-6">
                <InputField
                  label="Paystack Public Key"
                  value={settings.paystackPublicKey}
                  onChange={(e) => setSettings({ ...settings, paystackPublicKey: e.target.value })}
                  placeholder="pk_live_xxxxxxxxxxxxx"
                  hint="Used on the client-side for initializing payments."
                />
                <InputField
                  label="Paystack Secret Key"
                  type="password"
                  value={settings.paystackSecretKey}
                  onChange={(e) => setSettings({ ...settings, paystackSecretKey: e.target.value })}
                  placeholder="sk_live_xxxxxxxxxxxxx"
                  hint="Never share this key. Used for backend verification."
                />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden transition-all hover:shadow-md">
            <div className="px-8 py-6 border-b border-gray-100 bg-gray-50/50">
              <h2 className="text-xl font-bold text-darkNavy">Notifications & Legal</h2>
              <p className="text-sm text-gray-500 mt-0.5">Configure alerts and required document links</p>
            </div>
            <div className="p-8 space-y-2">
              <ToggleSwitch
                label="Email Notifications"
                description="Send automated emails for expiring subscriptions."
                checked={settings.enableEmailNotifications}
                onChange={(val) => setSettings({ ...settings, enableEmailNotifications: val })}
              />
              <ToggleSwitch
                label="Push Notifications"
                description="Send FCM push alerts to the mobile app."
                checked={settings.enablePushNotifications}
                onChange={(val) => setSettings({ ...settings, enablePushNotifications: val })}
              />
              
              <div className="pt-8 mt-4 border-t border-gray-100 space-y-6">
                <InputField
                  label="Privacy Policy URL"
                  value={settings.privacyPolicyUrl}
                  onChange={(e) => setSettings({ ...settings, privacyPolicyUrl: e.target.value })}
                  placeholder="https://example.com/privacy"
                />
                <InputField
                  label="Terms of Service URL"
                  value={settings.termsUrl}
                  onChange={(e) => setSettings({ ...settings, termsUrl: e.target.value })}
                  placeholder="https://example.com/terms"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Save Button */}
        <div className="md:hidden pt-4 pb-12">
          <button
            onClick={handleSave}
            disabled={saving}
            className="w-full flex justify-center items-center gap-2 px-6 py-4 bg-gradient-to-r from-primary to-indigo-600 text-white rounded-2xl font-bold shadow-lg disabled:opacity-50"
          >
            {saving ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <MdSave size={20} />}
            {saving ? 'Saving...' : 'Save All Settings'}
          </button>
        </div>

      </div>
    </div>
  );
};

export default Settings;
